import { useContext, useEffect } from 'react'

import FluxComposerStore from '~publish/legacy/composer/composer/stores/ComposerStore'

import { maybeEmitBridgeHeartbeat } from './bridgeHeartbeat'

import { ComposerContext } from '../ComposerContext'
import { installFluxToZustandMirror } from './installFluxToZustandMirror'
import { installZustandToFluxMirror } from './installZustandToFluxMirror'
import { registerOmniMirrorFlush } from './omniMirrorFlush'
import { registerOmniWriteTarget } from './omniWriteTarget'
import { projectFluxOmniContent } from './projectFluxOmniContent'

/**
 * Mount both mirror directions and the omni write target.
 *
 * `omniBridgeReady` flips true only after the seed and both mirrors are in
 * place, and back to false as the very first cleanup step. Readers
 * (`useDraftView`) key on it: effects run after render, so without the gate the
 * first render would serve the pre-seed (empty) `omni` while Flux still holds
 * the user's content.
 *
 * Cleanup order after that: the mirror-flush handle and write target first (so
 * neither the save path nor the text-sync plugin keeps reaching a tearing-down
 * bridge), then Zustand subscription, then Flux listener (which flushes its
 * pending debounced asset pass). This prevents a partially-torn-down state
 * where the plugin writes to Zustand but the mirror back to Flux is already
 * gone.
 *
 * Must be called inside `ComposerContext.Provider` (after store init) so the
 * store reference is available.
 */
export function useOmniBridge(): void {
  const store = useContext(ComposerContext)
  if (!store) {
    throw new Error('useOmniBridge must be used within ComposerProvider')
  }

  useEffect(() => {
    // Hydrate immediately from any Flux state that was set before this effect
    // ran (e.g. edit-mode init populates drafts synchronously before mount).
    const fluxOmni = FluxComposerStore.getDraft('omni')
    if (fluxOmni) {
      store.getState().syncOmniFromFlux(projectFluxOmniContent(fluxOmni))
    }

    const unregisterWriteTarget = registerOmniWriteTarget(store)
    const fluxMirror = installFluxToZustandMirror(store)
    const unsubscribeZustand = installZustandToFluxMirror(store, {
      flushPendingFluxMirror: fluxMirror.flush,
    })
    const unregisterMirrorFlush = registerOmniMirrorFlush(fluxMirror.flush)

    store.getState().setOmniBridgeReady(true)
    maybeEmitBridgeHeartbeat()

    return (): void => {
      store.getState().setOmniBridgeReady(false)
      unregisterMirrorFlush()
      unregisterWriteTarget()
      unsubscribeZustand()
      fluxMirror.unsubscribe()
    }
  }, [store])
}
